import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, Settings2 } from "lucide-react";
import { fmtPct, fmt1, fmt2 } from "../../../lib/finishing";
import { FinishingCutoffs } from "./FinishingCutoffs";

const Cell = ({ label, value, sub, tid }) => (
  <div className="border border-white/10 bg-[#0f0f0f] px-3 py-2" data-testid={tid}>
    <div className="text-[9px] uppercase tracking-[0.25em] text-[#757575]">{label}</div>
    <div className="font-mono text-base text-white">{value}</div>
    {sub && <div className="text-[9px] font-mono text-[#8a8a8a]">{sub}</div>}
  </div>
);
const Eff = ({ pps, synergy }) => `PPS ${fmt2(pps)}${synergy ? " · Synergy" : " · calc"}`;
const Needs = ({ tid }) => <div className="text-[10px] uppercase tracking-[0.25em] text-[#f59e0b]" data-testid={tid}>Needs Synergy</div>;
const Block = ({ code, title, children, tid }) => (
  <div className="border border-white/10 p-3" data-testid={tid}>
    <div className="flex items-center gap-2 mb-2"><span className="text-[9px] font-mono text-[var(--org-accent)]">{code}</span><span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white">{title}</span></div>
    {children}
  </div>
);
const Reading = ({ r, tid }) => r?.reading ? (
  <div className="mt-2 text-xs text-white" data-testid={tid}><span className="text-[#a0a0a0]">{r.volumeTier} × {r.finishingTier} →</span> {r.reading}</div>
) : <div className="mt-2 text-[10px] text-[#757575]">Not enough data for a reading.</div>;

// Section 1 · Stats panel. Read-only; informs the ratings; never rates anything.
export function FinishingStatsPanel({ playerId, records, season, setSeason, result, cutoffs, canEdit, onCutoffsSaved }) {
  const [showCutoffs, setShowCutoffs] = useState(false);
  const rec = records.find(r => r.season === season);
  return (
    <div className="border border-white/10 bg-[#141414] p-4 space-y-3" data-testid="fn-stats-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[9px] font-mono text-[var(--org-accent)]">1</span><div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">Stats panel</div>
        <div className="text-[10px] text-[#757575]">From Season Stats · informs the ratings, never rates</div>
        {records.length > 0 && (
          <select value={season || ""} onChange={e => setSeason(e.target.value)} className="bg-[#0f0f0f] border border-white/10 px-2 py-1 text-[11px] font-mono text-white focus:outline-none focus:border-[var(--org-accent)]" data-testid="fn-season-select">
            {records.map(r => <option key={r.season} value={r.season}>{r.season}{r.level ? ` · ${r.level}` : ""}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-3">
          {canEdit && <button onClick={() => setShowCutoffs(s => !s)} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] hover:text-white" data-testid="fn-cutoffs-toggle"><Settings2 className="w-3 h-3" /> Cutoffs</button>}
          <Link to={`/player/${playerId}?tab=season_stats`} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[var(--org-accent)] hover:underline" data-testid="fn-edit-season-stats">{rec ? "Edit in Season Stats" : "Add Season Stats"} <ExternalLink className="w-3 h-3" /></Link>
        </div>
      </div>
      {showCutoffs && canEdit && <FinishingCutoffs cutoffs={cutoffs} onSaved={(c) => { onCutoffsSaved(c); setShowCutoffs(false); }} />}
      {!rec || !result?.hasStats ? (
        <div className="text-xs text-[#a0a0a0]" data-testid="fn-no-stats">No Season Stats for this player{season ? ` (${season})` : ""} — add a season (Games + FGA at minimum) to see the readings.</div>
      ) : (
        <>
          <Block code="1A" title="Paint finishes" tid="fn-1a">
            {result.a ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] mb-2" data-testid="fn-1a-mode">{result.a.label}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Cell label="Per game" value={fmt1(result.a.perGame)} sub={`${result.a.attempts ?? "—"} attempts · ${result.games} games`} tid="fn-1a-per-game" />
                  <Cell label={result.a.mode === "proxy" ? "2P%" : "Paint FG%"} value={fmtPct(result.a.fgPct)} sub={`PPS ${fmt2(result.a.pps)}`} tid="fn-1a-pct" />
                  <Cell label="Volume tier" value={result.a.volumeTier || "—"} tid="fn-1a-volume" /><Cell label="Finishing tier" value={result.a.finishingTier || "—"} tid="fn-1a-finishing" />
                </div>
                <Reading r={result.a} tid="fn-1a-reading" />
              </>
            ) : <div className="text-[10px] text-[#757575]">Needs Games and FGA.</div>}
          </Block>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Block code="1B" title="Distribution" tid="fn-1b">
              {result.b ? (
                <>
                  <div className="text-xs text-white font-bold mb-2" data-testid="fn-1b-profile">{result.b.profile}</div>
                  <div className="space-y-1.5 text-[11px] font-mono">
                    <div className="flex justify-between gap-2"><span className="text-[#a0a0a0]">Rim</span><span className="text-white">{fmtPct(result.b.rim.share)} · {fmtPct(result.b.rim.fgPct)} · {fmt1(result.b.rim.perGame)}/g</span></div>
                    <div className="text-[9px] text-[#8a8a8a] text-right"><Eff {...result.b.rim} /></div>
                    <div className="flex justify-between gap-2"><span className="text-[#a0a0a0]">Mid paint</span><span className="text-white">{fmtPct(result.b.mid.share)} · {fmtPct(result.b.mid.fgPct)} · {fmt1(result.b.mid.perGame)}/g</span></div>
                    <div className="text-[9px] text-[#8a8a8a] text-right"><Eff {...result.b.mid} /></div>
                  </div>
                  <div className="mt-2 text-xs text-white" data-testid="fn-1b-reading">{result.b.reading}</div>
                </>
              ) : <Needs tid="fn-1b-needs" />}
            </Block>
            <Block code="1C" title="Rim" tid="fn-1c">
              {result.c ? (
                <>
                  <div className="grid grid-cols-2 gap-2"><Cell label="Per game" value={fmt1(result.c.perGame)} sub={`${result.c.attempts} att`} tid="fn-1c-per-game" /><Cell label="Rim FG%" value={fmtPct(result.c.fgPct)} sub={<Eff {...result.c} />} tid="fn-1c-pct" /></div>
                  <Reading r={result.c} tid="fn-1c-reading" />
                </>
              ) : <Needs tid="fn-1c-needs" />}
            </Block>
            <Block code="1D" title="Mid paint" tid="fn-1d">
              {result.d ? (
                <>
                  <div className="grid grid-cols-2 gap-2"><Cell label="Per game" value={fmt1(result.d.perGame)} sub={`${result.d.attempts} att`} tid="fn-1d-per-game" /><Cell label="Mid paint FG%" value={fmtPct(result.d.fgPct)} sub={`PPS ${fmt2(result.d.pps)}`} tid="fn-1d-pct" /></div>
                  <div className="mt-1.5 text-[10px] font-mono text-[#a0a0a0]" data-testid="fn-1d-split">Floater {fmtPct(result.d.floaterPct)} (PPS {fmt2(result.d.floaterPps)}) · Hook {fmtPct(result.d.hookPct)} (PPS {fmt2(result.d.hookPps)})</div>
                  <Reading r={result.d} tid="fn-1d-reading" />
                </>
              ) : <Needs tid="fn-1d-needs" />}
            </Block>
          </div>
          <Block code="1E" title="Flags" tid="fn-1e">
            <div className="text-[10px] font-mono text-[#8a8a8a] mb-1.5">FT rate {fmt2(result.metrics.ftRate)} · FT trips/g {fmt1(result.metrics.ftTrips)} · %SF {fmtPct(result.metrics.sf)} · TOV/g {fmt1(result.metrics.tovPg)}</div>
            {result.flags.length === 0 ? <div className="text-[10px] text-[#4ade80] uppercase tracking-[0.25em]" data-testid="fn-no-flags">No flags</div> : (
              <div className="space-y-1">{result.flags.map(f => <div key={f.id} className="flex items-center gap-2 text-xs text-[#f59e0b]" data-testid={`fn-flag-${f.id}`}><AlertTriangle className="w-3 h-3" /> {f.text}</div>)}</div>
            )}
          </Block>
        </>
      )}
    </div>
  );
}
